import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3,
  MessageSquareHeart,
  ArrowRight,
  Send,
  Loader2,
  DollarSign,
  Mail,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AdminHealthTile from "../components/AdminHealthTile";
import AdminShell from "../components/AdminShell";
import AdminTopline from "../components/AdminTopline";
import MembersPanel from "../components/MembersPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminHome — `/admin` landing. Dark "ops console" theme that is
 * visually distinct from the consumer app's cream/blush editorial.
 *
 * Gating: AdminShell checks `is_admin` for us — non-admins never see
 * this content.
 */

const AdminCard = ({ to, title, body, icon: Icon, badge, "data-testid": testId }) => (
  <Link
    to={to}
    className="group block bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-[#D4B483]/40 rounded-2xl p-6 transition-all"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="h-10 w-10 rounded-xl bg-[#D4B483]/15 flex items-center justify-center border border-[#D4B483]/20">
        <Icon className="h-4.5 w-4.5 text-[#D4B483]" />
      </div>
      {badge && (
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] bg-[#D4B483]/10 border border-[#D4B483]/20 rounded-full px-2.5 py-1">
          {badge}
        </span>
      )}
    </div>
    <h3 className="text-base font-medium text-white mb-2">{title}</h3>
    <p className="text-sm text-white/60 leading-relaxed mb-4">{body}</p>
    <span className="inline-flex items-center gap-1 text-sm text-[#D4B483] group-hover:gap-2 transition-all">
      Open
      <ArrowRight className="h-4 w-4" />
    </span>
  </Link>
);

const AdminHome = () => {
  const { user } = useAuth();
  const [digestState, setDigestState] = useState("idle"); // idle | sending

  const onSendDigest = async () => {
    setDigestState("sending");
    try {
      const { data } = await axios.post(
        `${API}/admin/digest/send-now`,
        {},
        { withCredentials: true },
      );
      if (data?.status === "sent") {
        toast.success(
          `Digest sent to ${data.recipient} · ${data.counts?.new_users || 0} new users, ${data.counts?.new_routines || 0} routines`,
        );
      } else if (data?.status === "skipped") {
        toast.info(`Digest skipped — ${data.reason || "already sent today"}.`);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Couldn't send digest. Check the logs.";
      toast.error(msg);
    } finally {
      setDigestState("idle");
    }
  };

  const firstName = user?.name?.split(" ")[0] || "founder";

  return (
    <AdminShell
      eyebrow="Overview"
      title={`Welcome back, ${firstName}`}
      subtitle="Live system health, analytics funnel, feedback inbox, and founder tools — all in one place."
    >
      <div data-testid="admin-home">
        {/* Live topline — revenue + sign-ups + MAU + activation.
            These are the four numbers that matter most at this stage
            of the company, so they live above everything else. Each
            tile is a Link to the deeper page for that metric. */}
        <AdminTopline />

        {/* Health tile */}
        <AdminHealthTile className="mb-8" />

        {/* Members panel — front-and-center so the second thing you
            see on /admin is who just signed up. */}
        <div className="mb-8">
          <MembersPanel />
        </div>

        {/* Primary cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <AdminCard
            to="/admin/revenue"
            title="Revenue & sign-ups"
            body="Lifetime + 30-day revenue, active Pro count, MRR estimate, sign-ups by method, what they came for, and the latest transactions."
            icon={DollarSign}
            badge="money"
            data-testid="admin-card-revenue"
          />
          <AdminCard
            to="/admin/analytics"
            title="Analytics dashboard"
            body="Funnel counts, drop-off, dwell-time buckets, cohort splits. Pick a window 1–90 days."
            icon={BarChart3}
            badge="data"
            data-testid="admin-card-analytics"
          />
          <AdminCard
            to="/admin/engagement"
            title="Engagement & traction"
            body="DAU/WAU/MAU, free-tier engagement, free→Pro funnel, weekly cohort retention, stealth-mode adoption. Investor-grade — aggregate-only."
            icon={TrendingUp}
            badge="investor"
            data-testid="admin-card-engagement"
          />
          <AdminCard
            to="/admin/feedback"
            title="Feedback inbox"
            body="Every star rating + free-text submission, freshest on top. TTS preview to pre-flight Mirror Mode."
            icon={MessageSquareHeart}
            badge="reviews"
            data-testid="admin-card-feedback"
          />
          <AdminCard
            to="/admin/waitlist"
            title="Mobile-app waitlist"
            body="Every iOS + Android waitlist signup with their 'why I signed up' reasons. One-click reactivation email + CSV export."
            icon={Mail}
            badge="launch"
            data-testid="admin-card-waitlist"
          />
        </div>

        {/* Secondary actions */}
        <div className="grid sm:grid-cols-2 gap-4">
          <button
            onClick={onSendDigest}
            disabled={digestState === "sending"}
            className="text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-[#D4B483]/40 rounded-2xl p-5 transition-all disabled:opacity-60 disabled:cursor-wait"
            data-testid="admin-digest-send-now-btn"
          >
            <div className="flex items-center gap-2 mb-2">
              {digestState === "sending" ? (
                <Loader2 className="h-4 w-4 animate-spin text-[#D4B483]" />
              ) : (
                <Send className="h-4 w-4 text-[#D4B483]" />
              )}
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483]">
                Daily digest
              </span>
            </div>
            <div className="text-sm text-white font-medium leading-snug mb-1">
              Send today's digest now
            </div>
            <div className="text-xs text-white/60 leading-relaxed">
              Runs automatically each morning. Tap to preview today's numbers
              in your inbox now — idempotent, won't double-send.
            </div>
          </button>
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 text-sm text-white/75 leading-relaxed">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                Founder note
              </span>
            </div>
            The admin console lives at{" "}
            <code className="bg-white/10 px-1.5 py-0.5 rounded text-[#D4B483]">
              /admin
            </code>{" "}
            — separate look from the consumer app so you never ship
            confusing screenshots. It's gated by the{" "}
            <code className="bg-white/10 px-1.5 py-0.5 rounded text-[#D4B483]">
              DEV_EMAILS
            </code>{" "}
            allowlist.
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminHome;
